import { useEffect, useState } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Globe,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

import seoService from "@/services/seoService";
import SEOForm from "./SEOForm";

const PAGE_LIST = [
  { value: "home", label: "Home", path: "/" },
  { value: "about", label: "About", path: "/about" },
  { value: "exam-pattern-and-syllabus", label: "Exam Pattern Andssyllabus", path: "/exam-pattern-and-syllabus" },
  { value: "blog", label: "blog", path: "/blog" },
  { value: "forgot-password", label: "forgot password", path: "/forgot-password" },
  { value: "program", label: "Program", path: "/program" },
  {
    value: "dubai-experience",
    label: "Dubai Experience",
    path: "/dubai-experience",
  },
  { value: "faqs", label: "FAQs", path: "/faqs" },
  { value: "contact", label: "Contact", path: "/contact" },
  {
    value: "privacy-policy",
    label: "Privacy Policy",
    path: "/privacy-policy",
  },
  {
    value: "refund-policy",
    label: "Refund Policy",
    path: "/refund-policy",
  },
  {
    value: "terms-conditions",
    label: "Terms & Conditions",
    path: "/terms-conditions",
  },
  { value: "login", label: "Candidate Login", path: "/login" },
  { value: "apply", label: "Apply", path: "/apply" },
  {
    value: "dashboard",
    label: "Candidate Dashboard",
    path: "/dashboard",
  },
  {
    value: "admin-dashboard",
    label: "Admin Dashboard",
    path: "/admin/dashboard",
  },
];

export default function SEO() {
  const [seoList, setSeoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSEO, setSelectedSEO] = useState(null);

  const loadSEO = async () => {
    try {
      setLoading(true);

      const response = await seoService.getAll();

      // Backend response compatibility
      const data =
        response?.data?.data ||
        response?.data ||
        [];

      setSeoList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("LOAD SEO ERROR:", error);

      toast.error(
        error?.response?.data?.message ||
          "Failed to load SEO data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSEO();
  }, []);

  const handleAdd = () => {
    setSelectedSEO(null);
    setModalOpen(true);
  };

  const handleEdit = (seo) => {
    setSelectedSEO(seo);
    setModalOpen(true);
  };

  const handleDelete = async (page) => {
    const pageInfo = PAGE_LIST.find(
      (item) => item.value === page
    );

    const confirmed = window.confirm(
      `Are you sure you want to delete SEO for "${
        pageInfo?.label || page
      }"?`
    );

    if (!confirmed) return;

    try {
      await seoService.delete(page);

      toast.success("SEO deleted successfully");

      loadSEO();
    } catch (error) {
      console.error("DELETE SEO ERROR:", error);

      toast.error(
        error?.response?.data?.message ||
          "Failed to delete SEO"
      );
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedSEO(null);
  };

  const handleSaved = () => {
    setModalOpen(false);
    setSelectedSEO(null);
    loadSEO();
  };

  const filteredSEO = seoList.filter((seo) => {
    const pageInfo = PAGE_LIST.find(
      (item) => item.value === seo.page
    );

    const pageName =
      pageInfo?.label ||
      seo.page ||
      "";

    const title =
      seo.metaTitle ||
      "";

    const keyword =
      Array.isArray(seo.focusKeywords)
        ? seo.focusKeywords.join(" ")
        : "";

    const searchText =
      `${pageName} ${title} ${keyword}`.toLowerCase();

    return searchText.includes(
      search.toLowerCase()
    );
  });

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">

        {/* HEADER */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

          <div>
            <div className="flex items-center gap-2">
              <Globe className="h-6 w-6 text-[#F5A623]" />

              <h1 className="text-2xl font-bold">
                SEO Management
              </h1>
            </div>

            <p className="mt-1 text-sm text-slate-400">
              Manage SEO settings for all CET pages.
            </p>
          </div>

          <button
            type="button"
            onClick={handleAdd}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#F5A623] px-4 py-2.5 text-sm font-bold text-slate-950 transition hover:bg-[#ffb638]"
          >
            <Plus className="h-4 w-4" />
            Add SEO
          </button>

        </div>

        {/* SEARCH */}
        <div className="mb-5">
          <div className="relative max-w-md">

            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />

            <input
              type="text"
              placeholder="Search page or SEO title..."
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              className="w-full rounded-lg border border-slate-700 bg-slate-900 py-2.5 pl-10 pr-4 text-sm text-white outline-none placeholder:text-slate-500 focus:border-[#F5A623]"
            />

          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">

          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400">
              <Loader2 className="mr-2 h-6 w-6 animate-spin" />
              Loading SEO...
            </div>
          ) : filteredSEO.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">

              <Globe className="mb-3 h-10 w-10 text-slate-600" />

              <h3 className="text-lg font-semibold text-slate-300">
                No SEO found
              </h3>

              <p className="mt-1 text-sm text-slate-500">
                Add SEO data for your pages.
              </p>

            </div>
          ) : (
            <div className="overflow-x-auto">

              <table className="w-full">

                <thead>
                  <tr className="border-b border-slate-800 text-left text-sm text-slate-400">

                    <th className="px-4 py-4">
                      Page
                    </th>

                    <th className="px-4 py-4">
                      Meta Title
                    </th>

                    <th className="px-4 py-4">
                      Description
                    </th>

                    <th className="px-4 py-4">
                      Status
                    </th>

                    <th className="px-4 py-4 text-right">
                      Actions
                    </th>

                  </tr>
                </thead>

                <tbody>

                  {filteredSEO.map((seo) => {

                    const pageInfo =
                      PAGE_LIST.find(
                        (item) =>
                          item.value === seo.page
                      );

                    return (
                      <tr
                        key={seo._id || seo.page}
                        className="border-b border-slate-800 transition hover:bg-slate-800/40"
                      >

                        {/* PAGE */}
                        <td className="px-4 py-4">

                          <p className="font-semibold text-[#F5A623]">
                            {pageInfo?.label ||
                              seo.page}
                          </p>

                          <p className="mt-1 text-xs text-slate-500">
                            {pageInfo?.path || "—"}
                          </p>

                        </td>

                        {/* TITLE */}
                        <td className="max-w-xs px-4 py-4">

                          <p className="truncate text-sm text-slate-200">
                            {seo.metaTitle ||
                              "Not set"}
                          </p>

                        </td>

                        {/* DESCRIPTION */}
                        <td className="max-w-sm px-4 py-4">

                          <p className="line-clamp-2 text-sm text-slate-400">
                            {seo.metaDescription ||
                              "Not set"}
                          </p>

                        </td>

                        {/* STATUS */}
                        <td className="px-4 py-4">

                          {seo.isActive ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-green-500/20 bg-green-500/10 px-2.5 py-1 text-xs text-green-400">
                              <CheckCircle className="h-3.5 w-3.5" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/20 bg-red-500/10 px-2.5 py-1 text-xs text-red-400">
                              <XCircle className="h-3.5 w-3.5" />
                              Inactive
                            </span>
                          )}

                        </td>

                        {/* ACTIONS */}
                        <td className="px-4 py-4">

                          <div className="flex items-center justify-end gap-2">

                            <button
                              type="button"
                              onClick={() =>
                                handleEdit(seo)
                              }
                              className="rounded-lg p-2 text-blue-400 transition hover:bg-blue-500/10"
                              title="Edit SEO"
                            >
                              <Edit className="h-4 w-4" />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                handleDelete(
                                  seo.page
                                )
                              }
                              className="rounded-lg p-2 text-red-400 transition hover:bg-red-500/10"
                              title="Delete SEO"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>

                          </div>

                        </td>

                      </tr>
                    );
                  })}

                </tbody>

              </table>

            </div>
          )}

        </div>

      </div>

      {/* ADD / EDIT MODAL */}
      {modalOpen && (
        <SEOForm
          seo={selectedSEO}
          onClose={handleModalClose}
          onSaved={handleSaved}
        />
      )}

    </div>
  );
}